import { StopOutlined, WarningOutlined } from "@ant-design/icons";
import { Space, TableProps, Tooltip } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import {
  PaymentStatus,
  PaymentStatusLabel,
  ShippingStatus,
  ShippingStatusLabel,
} from "../../../enum/lisence";
import expireIcon from "../../../images/expireIcon.svg";
import { DataType } from "../../../models/interface/license.interface";
import { formatDate } from "../../../utils/date";
import { ErrorMessages } from "../../../utils/constants/license";

export const ColumnsRequesting = (
  page: number,
  pageSize: number,
  total: number,
  requestedAtSortOrder: "ascend" | "descend" | null,
): TableProps<DataType>["columns"] => [
  {
    title: "",
    dataIndex: "",
    key: "",
    width: 50,
    render: (_, record) => {
      const isStuck =
        record.requestedAt &&
        new Date().getTime() - new Date(record.requestedAt).getTime() >
          4 * 60 * 60 * 1000;

      if (!isStuck) return null;

      return (
        <div className="flex justify-center items-center">
          <Tooltip
            placement="topLeft"
            title={
              <div>
                <div>License_id: {record.id}</div>
                <div>
                  {record.mailErrorMessage
                    ? ErrorMessages[record.mailErrorMessage]
                    : isStuck
                      ? ErrorMessages.NO_RESPONSE_FROM_SOFTGATE
                      : ErrorMessages.UNKNOWN_ERROR}
                </div>
              </div>
            }
          >
            {record.mailParseStatus === "failed" ? (
              <StopOutlined style={{ fontSize: 20 }} />
            ) : (
              <WarningOutlined style={{ fontSize: 20 }} />
            )}
          </Tooltip>
        </div>
      );
    },
  },
  {
    title: "#",
    dataIndex: "id",
    key: "id",
    width: 50,
    render: (_, __, index) => {
      const stt = (page - 1) * pageSize + index + 1;
      const formattedStt = String(stt).padStart(2, "0");
      return (
        <p className="mb-0 overflow-hidden text-ellipsis max-w-[50px] whitespace-nowrap">
          {formattedStt}
        </p>
      );
    },
  },
  {
    title: "HW Signature",
    dataIndex: "hardwareSignature",
    key: "hardwareSignature",
    width: 138,
    render: (hardwareSignature) => (
      <p className="mb-0 overflow-hidden text-ellipsis max-w-[138px] whitespace-nowrap">
        {hardwareSignature}
      </p>
    ),
  },
  {
    title: "カメラシリアルナンバー",
    dataIndex: "cameraSerial",
    key: "cameraSerial",
    width: 192,
  },
  {
    title: "HWシリアルナンバー",
    dataIndex: "assignedDeviceSerial",
    key: "assignedDeviceSerial",
    width: 180,
  },
  {
    title: "有効期限",
    dataIndex: "expiresAt",
    key: "expiresAt",
    render: (_, { expiresAt }) => {
      if (!expiresAt) return <span>—</span>;
      return <span>{formatDate(expiresAt)}</span>;
    },
    width: 140,
  },
  {
    title: "Agent Name",
    dataIndex: "assignedAgent",
    key: "assignedAgent",
    width: 108,
    render: (assignedAgent) => (
      <p className="mb-0 overflow-hidden text-ellipsis max-w-[108px] whitespace-nowrap">
        {assignedAgent}
      </p>
    ),
  },
  {
    title: "申請日",
    dataIndex: "requestedAt",
    key: "requestedAt",
    sorter: true,
    sortOrder: requestedAtSortOrder,
    render: (_, { requestedAt }) => {
      if (!requestedAt) return <span>—</span>;
      return <span>{formatDate(requestedAt)}</span>;
    },
    width: 140,
  },
  {
    title: "softgateへの支払ステータス",
    dataIndex: "status",
    key: "status",
    render: (_, { batch }) => {
      if (batch.paymentStatus === PaymentStatus.PAID) {
        return (
          <div className="rounded-md w-[86px] h-[26px] bg-[#77BF4033] text-center">
            <p className="text-sm font-normal text-[#098906] mb-0 leading-[26px]">
              支払い済み
            </p>
          </div>
        );
      }
      if (batch.paymentStatus === PaymentStatus.UNPAID) {
        return (
          <div className="rounded-md w-[94px] h-[26px] bg-[#FFA90933] text-center">
            <p className="text-sm font-normal text-[#FF8B01] mb-0 leading-[26px]">
              未払い
            </p>
          </div>
        );
      }
      return (
        <div className="rounded-md w-[86px] h-[26px] bg-[#FF978B33] text-center">
          <p className="text-sm font-normal text-[#FF5646] mb-0 leading-[26px]">
            キャンセル
          </p>
        </div>
      );
    },
    width: 222,
  },
  {
    title: "アクション",
    key: "action",
    render: (_, record) => (
      <Space size="middle">
        <Link
          to={`/admin/license-management/${record.id}`}
          className="base-button w-[75px] h-[41px] rounded  flex justify-center items-center"
        >
          詳細
        </Link>
      </Space>
    ),
    width: 104,
  },
];

export const ColumnsActivated = (
  page: number,
  pageSize: number,
  total: number,
  requestedAtSortOrder: "ascend" | "descend" | null,
): TableProps<DataType>["columns"] => [
  {
    title: "#",
    dataIndex: "id",
    key: "id",
    width: 50,
    render: (_, __, index) => {
      const stt = (page - 1) * pageSize + index + 1;
      const formattedStt = String(stt).padStart(2, "0");
      return (
        <p className="mb-0 overflow-hidden text-ellipsis max-w-[50px] whitespace-nowrap">
          {formattedStt}
        </p>
      );
    },
  },
  {
    title: "HW Signature",
    dataIndex: "hardwareSignature",
    key: "hardwareSignature",
    width: 138,
    render: (hardwareSignature) => (
      <p className="mb-0 overflow-hidden text-ellipsis max-w-[138px] whitespace-nowrap">
        {hardwareSignature}
      </p>
    ),
  },
  {
    title: "カメラシリアルナンバー",
    dataIndex: "cameraSerial",
    key: "cameraSerial",
    width: 192,
  },
  {
    title: "HWシリアルナンバー",
    dataIndex: "assignedDeviceSerial",
    key: "assignedDeviceSerial",
    width: 180,
  },
  {
    title: "有効期限",
    dataIndex: "expiresAt",
    key: "expiresAt",
    render: (_, { expiresAt }) => {
      if (!expiresAt) return <span>—</span>;
      const isNearExpiry = dayjs(expiresAt).diff(dayjs(), "day") < 90;
      return (
        <span
          className={isNearExpiry ? "text-[#FF5646] flex gap-1" : undefined}
        >
          {isNearExpiry && <img src={expireIcon} alt="expireIcon" />}
          {formatDate(expiresAt)}
        </span>
      );
    },
    width: 150,
  },
  {
    title: "Agent Name",
    dataIndex: "assignedAgent",
    key: "assignedAgent",
    width: 108,
    render: (assignedAgent) => (
      <p className="mb-0 overflow-hidden text-ellipsis max-w-[108px] whitespace-nowrap">
        {assignedAgent}
      </p>
    ),
  },
  {
    title: "申請日",
    dataIndex: "requestedAt",
    key: "requestedAt",
    sorter: true,
    sortOrder: requestedAtSortOrder,
    render: (_, { requestedAt }) => {
      if (!requestedAt) return <span>—</span>;
      return <span>{formatDate(requestedAt)}</span>;
    },
    width: 140,
  },
  {
    title: "決済ステータス",
    dataIndex: "status",
    key: "status",
    render: (_, { batch }) => {
      if (batch.paymentStatus === PaymentStatus.PAID) {
        return (
          <div className="rounded-md w-[86px] h-[26px] bg-[#77BF4033] text-center">
            <p className="text-sm font-normal text-[#098906] mb-0 leading-[26px]">
              支払い済み
            </p>
          </div>
        );
      }
      if (batch.paymentStatus === PaymentStatus.UNPAID) {
        return (
          <div className="rounded-md w-[94px] h-[26px] bg-[#FFA90933] text-center">
            <p className="text-sm font-normal text-[#FF8B01] mb-0 leading-[26px]">
              {PaymentStatusLabel.unpaid}
            </p>
          </div>
        );
      }
    },
    width: 160,
  },
  {
    title: "出荷ができるか",
    dataIndex: "shipping",
    key: "shipping",
    render: (_, { batch }) => {
      if (batch.shippingStatus === ShippingStatus.NOT_SHIPPED) {
        return (
          <div className="rounded-md w-[94px] h-[26px] bg-[#FFA90933] text-center">
            <p className="text-sm font-normal text-[#FF8B01] mb-0 leading-[26px]">
              {ShippingStatusLabel.not_shipped}
            </p>
          </div>
        );
      }
      if (batch.shippingStatus === ShippingStatus.SHIPPED) {
        return (
          <div className="rounded-md w-[86px] h-[26px] bg-[#77BF4033] text-center">
            <p className="text-sm font-normal text-[#098906] mb-0 leading-[26px]">
              {ShippingStatusLabel.shipped}
            </p>
          </div>
        );
      }
    },
    width: 160,
  },
  {
    title: "アクション",
    key: "action",
    render: (_, record) => (
      <Space size="middle">
        <Link
          to={`/admin/license-management/${record.id}`}
          className="base-button w-[75px] h-[41px] rounded  flex justify-center items-center"
        >
          詳細
        </Link>
      </Space>
    ),
    width: 104,
  },
];

export const ColumnsDiscard = (
  page: number,
  pageSize: number,
  total: number,
  requestedAtSortOrder: "ascend" | "descend" | null,
): TableProps<DataType>["columns"] => [
  {
    title: "#",
    dataIndex: "id",
    key: "id",
    width: 50,
    render: (_, __, index) => {
      const stt = (page - 1) * pageSize + index + 1;
      const formattedStt = String(stt).padStart(2, "0");
      return (
        <p className="mb-0 overflow-hidden text-ellipsis max-w-[50px] whitespace-nowrap">
          {formattedStt}
        </p>
      );
    },
  },
  {
    title: "カメラシリアルナンバー",
    dataIndex: "cameraSerial",
    key: "cameraSerial",
    width: 192,
    render: (cameraSerial) => (
      <p className="mb-0 overflow-hidden text-ellipsis max-w-[192px] whitespace-nowrap">
        {cameraSerial}
      </p>
    ),
  },
  {
    title: "HWシリアルナンバー",
    dataIndex: "assignedDeviceSerial",
    key: "assignedDeviceSerial",
    width: 179,
    render: (assignedDeviceSerial) => (
      <p className="mb-0 overflow-hidden text-ellipsis max-w-[179px] whitespace-nowrap">
        {assignedDeviceSerial}
      </p>
    ),
  },
  {
    title: "有効期限",
    dataIndex: "expiresAt",
    key: "expiresAt",
    render: (_, { expiresAt }) => {
      if (!expiresAt) return <span>—</span>;
      return <span>{formatDate(expiresAt)}</span>;
    },
    width: 122,
  },
  {
    title: "Agent Name",
    dataIndex: "assignedAgent",
    key: "assignedAgent",
    width: 108,
    render: (assignedAgent) => (
      <p className="mb-0 overflow-hidden text-ellipsis max-w-[108px] whitespace-nowrap">
        {assignedAgent}
      </p>
    ),
  },
  {
    title: "申請日",
    dataIndex: "requestedAt",
    key: "requestedAt",
    sorter: true,
    sortOrder: requestedAtSortOrder,
    render: (_, { requestedAt }) => {
      if (!requestedAt) return <span>—</span>;
      return <span>{formatDate(requestedAt)}</span>;
    },
    width: 121,
  },
  {
    title: "アクション",
    key: "action",
    render: (_, record) => (
      <Space size="middle">
        <Link
          to={`/admin/license-management/${record.id}`}
          className="base-button w-[75px] h-[41px] rounded  flex justify-center items-center"
        >
          詳細
        </Link>
      </Space>
    ),
    width: 104,
  },
];
